import type { Maybe } from '@adornis/base/utilTypes.js';
import { RXController } from '@adornis/chemistry/controllers/RXController.js';
import { XSnackbar } from '@adornis/chemistry/elements/components/x-snackbar.js';
import { XMultiEntityPicker } from '@adornis/entity-picker/client/x-multi-entity-picker.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import type { AbstractAspect } from '@adornis/filter/aspect-schemas/AbstractAspect.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import '@adornis/fonts/fonts.js';
import { UserRole } from '@ils/static/enums.js';
import { customElement, property } from 'lit/decorators.js';
import { map } from 'rxjs';
import { subscribeSearchUser } from '../../api/user.js';
import { Or } from '../../db/filter/or.js';
import { ILSUser } from '../../db/ils-user.js';

/**
 * @element ils-user-multi-selection
 */
@customElement('ils-user-multi-selection')
export class IlsUserSelection extends XMultiEntityPicker<typeof ILSUser> {
  @property({ attribute: false })
  selectedLocation = new RXController(this, '');

  constructor() {
    super();
    this.selectedLocation.observable.subscribe(value => {
      if (this.value.value) {
        const validUsers = this._entities.value
          .filter(user => {
            const examRoles = user.roles
              .filter(role => [UserRole.EXAMCENTER, UserRole.EXAMCENTERMANAGER].includes(role.name))
              .flatMap(role => role.contexts);
            return examRoles.includes(value);
          })
          .map(u => u._id);
        if (validUsers.length !== this.value.value.length) {
          void XSnackbar.show(
            `Es wurden ${
              this.value.value.length - validUsers.length
            } Koordinatoren entfernt, weil sie nicht dieser Location zugeordnet sind.`,
          );
          this.value.next(validUsers);
          this.dispatchEvent(new CustomEvent('value-picked', { detail: { value: validUsers } }));
        }
      }
      const aspects: AbstractAspect[] = [
        new Or({
          fieldName: 'user.roles.name',
          filters: [
            new StringAspect({ fieldName: 'user.roles.name', mode: 'contains', value: UserRole.EXAMCENTER }),
            new StringAspect({ fieldName: 'user.roles.name', mode: 'contains', value: UserRole.ADMIN }),
          ],
        }),
      ];
      if (value) {
        aspects.push(new StringAspect({ fieldName: 'user.roles.contexts', mode: 'equals', value }));
      }
      this.options.next({
        class: ILSUser,
        operation: (skip, limit, filter) => gqlFields => {
          return subscribeSearchUser(
            skip,
            limit,
            filter,
          )({ user: gqlFields }).pipe(map(users => users.map(user => user.user)));
        },
        filter: new AdornisFilter({
          searchFields: ['user.email', 'user.ecommerce.firstName', 'user.ecommerce.lastName'],
          aspects,
        }),
        selectionSet: { ecommerce: { firstName: 1, lastName: 1 }, email: 1, roles: { name: 1, contexts: 1 } },
      });
    });
  }

  override renderString = (user: Maybe<ILSUser>) =>
    user ? `${user.ecommerce?.firstName} ${user.ecommerce?.lastName} (${user.email})` : '';
}
